import React, { useCallback, useEffect, useState } from 'react';

import type { NumberInputProps } from './types';
import { filterNumericInput, formatNumber, parseNumber } from './util';
import { Label } from '../../general/Label';
import { cn } from '../../utils/cn';

export type { NumberInputProps } from './types';

/**
 * A form number input component that only accepts numeric values.
 *
 * @param id - ID of the input element. Used for htmlFor association.
 * @param value - The current numeric value.
 * @param onChange - Callback fired when the value changes.
 * @param label - Label text for the input.
 * @param layout - Layout direction: "horizontal" (default) or "vertical".
 * @param error - Error message to display below the input.
 * @param measuringUnit - Measuring unit to display on the right side of the input.
 * @param min - Minimum allowed value.
 * @param max - Maximum allowed value.
 * @param step - Step value for increment/decrement.
 * @param allowDecimals - Whether to allow decimal numbers (default: true).
 * @param placeholder - Placeholder text for the input.
 * @param disabled - Whether the input is disabled.
 * @param className - Additional CSS classes for the container.
 * @param labelClassName - Additional CSS classes for the label.
 * @param inputClassName - Additional CSS classes for the input wrapper.
 * @param ...props - Additional HTML input attributes.
 *
 * @example
 * // Basic usage
 * <NumberInput label="Anzahl" id="count" value={count} onChange={setCount} />
 *
 * @example
 * // With min/max constraints
 * <NumberInput label="Menge" id="quantity" min={0} max={100} value={qty} onChange={setQty} />
 *
 * @example
 * // Integer only
 * <NumberInput label="Stück" id="pieces" allowDecimals={false} value={pieces} onChange={setPieces} />
 *
 * @example
 * // With error state
 * <NumberInput label="Anzahl" id="count" value={count} onChange={setCount} error="Ungültige Zahl" />
 */
export const NumberInput: React.FC<NumberInputProps> = ({
    allowDecimals = true,
    className,
    disabled,
    error,
    id,
    inputClassName,
    label,
    labelClassName,
    layout = 'horizontal',
    max,
    min,
    onChange,
    step,
    value,
    ...baseProps
}) => {
    const isHorizontal = layout === 'horizontal';
    const hasError = Boolean(error);

    let props = baseProps;

    let measuringUnit: React.ReactNode | undefined;
    if ('measuringUnit' in baseProps) {
        const { measuringUnit: measuringUnitProps, ...rest } = baseProps;
        measuringUnit = measuringUnitProps;
        props = rest;
    }

    const [displayValue, setDisplayValue] = useState<string>('');

    useEffect(() => {
        setDisplayValue(formatNumber(value));
    }, [value]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const rawValue = e.target.value;

            if (rawValue === '') {
                setDisplayValue('');
                onChange?.(null);
                return;
            }

            const filteredValue = filterNumericInput(rawValue, allowDecimals);
            setDisplayValue(filteredValue);

            const numericValue = parseNumber(filteredValue);
            onChange?.(numericValue);
        },
        [onChange, allowDecimals],
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            let numericValue = parseNumber(displayValue);

            if (numericValue !== null) {
                if (min !== undefined && numericValue < min) {
                    numericValue = min;
                }

                if (max !== undefined && numericValue > max) {
                    numericValue = max;
                }

                setDisplayValue(formatNumber(numericValue));

                if (numericValue !== value) {
                    onChange?.(numericValue);
                }
            } else if (displayValue === '' && value !== null && value !== undefined) {
                onChange?.(null);
            }

            props.onBlur?.(e);
        },
        [displayValue, value, min, max, onChange, props],
    );

    return (
        <div
            className={cn(
                'flex',
                isHorizontal ? 'flex-row gap-2.5 items-start' : 'flex-col gap-2 items-start',
                className,
            )}
        >
            {label && (
                <Label
                    htmlFor={id}
                    className={cn(
                        isHorizontal ? 'w-[125px] shrink-0 justify-end h-8' : 'w-full px-0 py-0 justify-start',
                        labelClassName,
                    )}
                >
                    {label}
                </Label>
            )}
            <div className={cn('flex flex-col', isHorizontal ? 'flex-1' : 'w-full')}>
                <div
                    className={cn(
                        'h-8 w-full',
                        'flex items-center',
                        'pl-3',
                        'bg-white border rounded',
                        hasError ? 'border-alert-error' : 'border-grey-dark',
                        'focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-focus focus-within:ring-offset-1',
                        disabled && 'cursor-not-allowed bg-surface-light opacity-50',
                        inputClassName,
                    )}
                >
                    <input
                        id={id}
                        type="text"
                        inputMode={allowDecimals ? 'decimal' : 'numeric'}
                        disabled={disabled}
                        value={displayValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        min={min}
                        max={max}
                        step={step}
                        className={cn(
                            'flex-1 min-w-0 h-full',
                            'bg-transparent border-none outline-none',
                            'text-sm text-midnight',
                            'placeholder:text-midnight-placeholder',
                            'disabled:cursor-not-allowed',
                        )}
                        {...props}
                        autoComplete="off"
                        data-1p-ignore
                    />
                    {measuringUnit && (
                        <div className="flex items-center shrink-0 h-full">
                            <span className="border-l border-grey-dark h-4/6" aria-hidden="true" />
                            <span
                                className={cn(
                                    'flex items-center justify-center h-5 w-8 shrink-0 text-sm text-midnight select-none',
                                    disabled && 'opacity-50',
                                )}
                                aria-hidden={true}
                            >
                                {measuringUnit}
                            </span>
                        </div>
                    )}
                </div>
                {hasError && <span className="text-[10px] leading-[1.46] text-alert-error mt-1">{error}</span>}
            </div>
        </div>
    );
};
